import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { DrawerShell } from 'src/components/atlas/molecules/Drawer/DrawerShell';
import { ConfirmationBeforeRemoveModal } from 'src/components/atlas/organisms/ConfirmationBeforeRemoveModal/confirmationBeforeRemoveModal';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { useGetOrganisationCustomFieldSectionsQuery } from 'src/graphql/generated';
import { CustomFieldAuditLog } from './components/CustomFieldAuditLog';
import { CustomFieldDrawerTitle } from './components/CustomFieldDrawerTitle';
import { OptionsManager } from './components/OptionsManager';
import type { CustomField } from './CustomFields';
import { useCustomFieldMutations } from './hooks/useCustomFieldMutations';
import { handleCustomFieldError } from './utils/errorHandling';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_IDENTIFIER_LENGTH,
  MAX_LABEL_LENGTH,
  suggestIdentifierFrontend,
  validateCustomFieldForm,
} from './utils/validation';
import {
  CustomFieldOption,
  CustomFieldOptionsEditor,
} from './CustomFieldOptionsEditor';
import { flushOptionChanges } from './utils/flushOptionChanges';

interface CreateCustomFieldSideSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  mode?: 'create' | 'edit';
  fieldToEdit?: CustomField;
}

type FieldType = 'TEXT' | 'SINGLE_SELECT';

export const CreateCustomFieldSideSheet: React.FC<
  CreateCustomFieldSideSheetProps
> = ({ isOpen, onClose, onSuccess, mode = 'create', fieldToEdit }) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;

  const isEditMode = mode === 'edit';

  const [label, setLabel] = useState(fieldToEdit?.label || '');
  const [identifier, setIdentifier] = useState(fieldToEdit?.identifier || '');
  const [description, setDescription] = useState(
    fieldToEdit?.description || '',
  );
  const [fieldType, setFieldType] = useState<FieldType>(
    (fieldToEdit?.fieldType as FieldType) || 'TEXT',
  );
  const [options, setOptions] = useState<string[]>(fieldToEdit?.options || []);
  const [currentOption, setCurrentOption] = useState('');
  // Edit mode only: staged option edits (add/retire/restore/reorder) applied on
  // Save. `optionDraft` is the working set; `initialOptions` is the loaded
  // baseline used to diff what to persist.
  const [optionDraft, setOptionDraft] = useState<CustomFieldOption[]>(
    fieldToEdit?.optionDetails ?? [],
  );
  const [initialOptions, setInitialOptions] = useState<CustomFieldOption[]>(
    fieldToEdit?.optionDetails ?? [],
  );
  const [sectionId, setSectionId] = useState<string>(
    fieldToEdit?.sectionId || '',
  );
  const [sectionIdTouched, setSectionIdTouched] = useState(false);

  const { data: sectionsData } = useGetOrganisationCustomFieldSectionsQuery();
  const sections = useMemo(
    () => sectionsData?.getOrganisationCustomFieldSections?.sections ?? [],
    [sectionsData],
  );

  const [labelError, setLabelError] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [optionsError, setOptionsError] = useState('');
  const [identifierTouched, setIdentifierTouched] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'audit'>('details');

  const {
    createCustomField,
    updateCustomField,
    deactivateCustomField,
    reactivateCustomField,
    isLoading,
  } = useCustomFieldMutations();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!identifierTouched && label && !isEditMode) {
      setIdentifier(suggestIdentifierFrontend(label));
    }
  }, [label, identifierTouched, isEditMode]);

  useEffect(() => {
    if (!isOpen || isEditMode || fieldToEdit || sectionIdTouched) return;
    const found = sections.find((section) => section.isDefault);
    setSectionId(found?.id ?? '');
  }, [isOpen, sections, isEditMode, fieldToEdit, sectionIdTouched]);

  useEffect(() => {
    if (fieldToEdit && isOpen) {
      setLabel(fieldToEdit.label);
      setIdentifier(fieldToEdit.identifier);
      setDescription(fieldToEdit.description || '');
      setFieldType(fieldToEdit.fieldType as FieldType);
      setOptions(fieldToEdit.options || []);
      setOptionDraft(fieldToEdit.optionDetails ?? []);
      setInitialOptions(fieldToEdit.optionDetails ?? []);
      setSectionId(fieldToEdit.sectionId || '');
      setIdentifierTouched(true);
    }
  }, [fieldToEdit, isOpen]);

  const handleLabelChange = (value: string) => {
    setLabel(value.slice(0, MAX_LABEL_LENGTH));
    if (labelError) setLabelError('');
  };

  const handleIdentifierChange = (value: string) => {
    setIdentifierTouched(true);
    setIdentifier(value);
    if (identifierError) setIdentifierError('');
  };

  const validateForm = (): boolean => {
    // In edit mode option changes are staged in `optionDraft` (not the
    // create-mode `options` string[]), so validate against the staged active
    // labels — otherwise restoring/retiring wouldn't be reflected and a field
    // whose active options are momentarily empty would fail validation.
    const optionLabelsToValidate = isEditMode
      ? optionDraft.filter((opt) => opt.isActive).map((opt) => opt.label)
      : options;

    const result = validateCustomFieldForm(
      label,
      identifier,
      fieldType,
      optionLabelsToValidate,
    );

    if (result.labelError) setLabelError(result.labelError);
    if (result.identifierError) setIdentifierError(result.identifierError);
    if (result.optionsError) setOptionsError(result.optionsError);

    return result.isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode && fieldToEdit) {
        await updateCustomField({
          id: fieldToEdit.id,
          label: label.trim(),
          description: description.trim() || null,
          sectionId: sectionId || null,
        });

        if (fieldType === 'SINGLE_SELECT') {
          await flushOptionChanges(fieldToEdit.id, initialOptions, optionDraft);
          // updateCustomField already invalidated the list, but that fires
          // before the option flush above — re-invalidate so the table (and
          // the field passed back into this sheet on re-edit) reflects the
          // persisted option state, not the pre-flush snapshot.
          await queryClient.invalidateQueries(['GetOrganisationCustomFields']);
        }

        handleSuccessToast({
          toastMessage: 'Custom field updated successfully',
        });
      } else {
        const nonEmptyOptions =
          fieldType === 'SINGLE_SELECT'
            ? options.filter((opt) => opt.trim())
            : undefined;

        await createCustomField({
          label: label.trim(),
          identifier: identifierTouched ? identifier : undefined,
          description: description.trim() || null,
          fieldType,
          options: nonEmptyOptions,
          sectionId: sectionId || null,
        });

        handleSuccessToast({
          toastMessage: 'Custom field created successfully',
        });
      }

      handleClose();
      onSuccess?.();
    } catch (error) {
      handleCustomFieldError(error as Error, isEditMode, {
        setLabelError,
        setIdentifierError,
        setOptionsError,
      });
    }
  };

  const handleDeactivateClick = () => {
    setIsDeactivateModalOpen(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!fieldToEdit) return;

    try {
      await deactivateCustomField({ id: fieldToEdit.id });
      handleSuccessToast({
        toastMessage: 'Custom field deactivated successfully',
      });
      setIsDeactivateModalOpen(false);
      handleClose();
      onSuccess?.();
    } catch (error) {
      handleErrorToast({ toastMessage: 'Error deactivating custom field' });
      setIsDeactivateModalOpen(false);
    }
  };

  const handleReactivateClick = () => {
    setIsReactivateModalOpen(true);
  };

  const handleConfirmReactivate = async () => {
    if (!fieldToEdit) return;

    try {
      await reactivateCustomField({ id: fieldToEdit.id });
      handleSuccessToast({
        toastMessage: 'Custom field restored successfully',
      });
      setIsReactivateModalOpen(false);
      handleClose();
      onSuccess?.();
    } catch (error) {
      handleErrorToast({ toastMessage: 'Error restoring custom field' });
      setIsReactivateModalOpen(false);
    }
  };

  const handleClose = () => {
    setLabel('');
    setIdentifier('');
    setDescription('');
    setFieldType('TEXT');
    setOptions([]);
    setOptionDraft([]);
    setInitialOptions([]);
    setCurrentOption('');
    setSectionId('');
    setSectionIdTouched(false);
    setLabelError('');
    setIdentifierError('');
    setOptionsError('');
    setIdentifierTouched(false);
    setActiveTab('details');
    onClose();
  };

  const isAuditTab = isEditMode && activeTab === 'audit';
  const isSaveDisabled = isAuditTab || !label.trim() || isLoading;
  const isInactiveField = fieldToEdit?.status === 'INACTIVE';

  const drawerTitle =
    isEditMode && fieldToEdit ? (
      <CustomFieldDrawerTitle field={fieldToEdit} />
    ) : (
      'Create custom field'
    );

  return (
    <>
      <DrawerShell
        open={isOpen}
        title={drawerTitle}
        onRequestClose={handleClose}
        onNext={handleSave}
        nextDisabled={isSaveDisabled}
        nextTitle={isEditMode ? 'Save changes' : 'Create field'}
        cancelTitle={
          isEditMode
            ? isInactiveField
              ? 'Restore field'
              : 'Retire field'
            : 'Cancel'
        }
        onCustomCancel={
          isEditMode
            ? isInactiveField
              ? handleReactivateClick
              : handleDeactivateClick
            : undefined
        }
        cancelButtonState={isEditMode ? 'danger' : 'default'}
        onBack={isEditMode ? handleClose : undefined}
        backTitle={isEditMode ? 'Cancel' : undefined}
        testId="create-custom-field-side-sheet"
      >
        {isEditMode && fieldToEdit && (
          <Tabs
            value={activeTab}
            onChange={(_, next: 'details' | 'audit') => setActiveTab(next)}
            sx={{ mb: '20px' }}
          >
            <Tab
              label="Details"
              value="details"
              data-testid="custom-field-tab-details"
            />
            <Tab
              label="Audit log"
              value="audit"
              data-testid="custom-field-tab-audit"
            />
          </Tabs>
        )}

        {isAuditTab && fieldToEdit && (
          <CustomFieldAuditLog customFieldId={fieldToEdit.id} />
        )}

        <Box
          sx={{
            display: isAuditTab ? 'none' : 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                mb: '4px',
              }}
            >
              <Typography
                component="label"
                htmlFor="custom-field-label-input"
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                }}
              >
                Label <span style={{ color: color.type.muted.value }}>*</span>
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: '12px', color: color.type.muted.value }}
              >
                {label.length}/{MAX_LABEL_LENGTH}
              </Typography>
            </Box>
            <TextField
              id="custom-field-label-input"
              fullWidth
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder="Enter field label"
              data-testid="create-custom-field-label-input"
              inputProps={{ maxLength: MAX_LABEL_LENGTH }}
              error={!!labelError}
            />
            {labelError && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: color.status.error.default.value,
                  mt: '4px',
                }}
                data-testid="create-custom-field-label-error"
              >
                {labelError}
              </Typography>
            )}
            {!labelError && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: color.type.muted.value,
                  mt: '4px',
                }}
              >
                Shown to case managers on the case page
              </Typography>
            )}
          </Box>

          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                mb: '4px',
              }}
            >
              <Typography
                component="label"
                htmlFor="custom-field-identifier-input"
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                }}
              >
                Identifier{' '}
                <span style={{ color: color.type.muted.value }}>*</span>
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: '12px', color: color.type.muted.value }}
              >
                {identifier.length}/{MAX_IDENTIFIER_LENGTH}
              </Typography>
            </Box>
            <TextField
              id="custom-field-identifier-input"
              fullWidth
              value={identifier}
              onChange={(e) => handleIdentifierChange(e.target.value)}
              placeholder="Auto-generated from label"
              data-testid="create-custom-field-identifier-input"
              error={!!identifierError}
              disabled={isEditMode}
            />
            {identifierError && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: color.status.error.default.value,
                  mt: '4px',
                }}
                data-testid="create-custom-field-identifier-error"
              >
                {identifierError}
              </Typography>
            )}
            {!identifierError && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: color.type.muted.value,
                  mt: '4px',
                }}
              >
                Unique identifier, locked after creation. Used by APIs, imports,
                and exports.
              </Typography>
            )}
          </Box>

          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                mb: '4px',
              }}
            >
              <Typography
                component="label"
                htmlFor="custom-field-description-input"
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                }}
              >
                Description
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: '12px', color: color.type.muted.value }}
              >
                {description.length}/{MAX_DESCRIPTION_LENGTH}
              </Typography>
            </Box>
            <TextField
              id="custom-field-description-input"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) =>
                setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))
              }
              placeholder="Explain when to use this field."
              data-testid="create-custom-field-description-input"
              inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                component="label"
                htmlFor="custom-field-type-select"
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                  mb: '4px',
                  display: 'block',
                }}
              >
                Field type{' '}
                <span style={{ color: color.type.muted.value }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  id="custom-field-type-select"
                  value={fieldType}
                  onChange={(e) => setFieldType(e.target.value as FieldType)}
                  data-testid="create-custom-field-type-select"
                  displayEmpty
                  disabled={isEditMode}
                >
                  <MenuItem value="" disabled>
                    Choose an option
                  </MenuItem>
                  <MenuItem value="TEXT">Text input</MenuItem>
                  <MenuItem value="SINGLE_SELECT">Single select</MenuItem>
                </Select>
              </FormControl>
              {isEditMode && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: color.type.muted.value,
                    mt: '4px',
                  }}
                >
                  Type cannot be changed after values exist. Retire this field
                  and create a replacement instead.
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                component="label"
                htmlFor="custom-field-section-select"
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                  mb: '4px',
                  display: 'block',
                }}
              >
                Section
              </Typography>
              <FormControl fullWidth>
                <Select
                  id="custom-field-section-select"
                  value={sectionId}
                  onChange={(e) => {
                    setSectionId(e.target.value);
                    setSectionIdTouched(true);
                  }}
                  data-testid="create-custom-field-section-select"
                  displayEmpty
                >
                  <MenuItem value="">Ungrouped</MenuItem>
                  {sections.map((section) => (
                    <MenuItem key={section.id} value={section.id}>
                      {section.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: color.type.muted.value,
                  mt: '4px',
                }}
              >
                Groups this field on the case page.
              </Typography>
            </Box>
          </Box>

          {fieldType === 'SINGLE_SELECT' && !isEditMode && (
            <OptionsManager
              options={options}
              currentOption={currentOption}
              optionsError={optionsError}
              onOptionsChange={setOptions}
              onCurrentOptionChange={setCurrentOption}
              onOptionsErrorChange={setOptionsError}
            />
          )}

          {fieldType === 'SINGLE_SELECT' && isEditMode && fieldToEdit && (
            <CustomFieldOptionsEditor
              customFieldId={fieldToEdit.id}
              options={optionDraft}
              onOptionsChange={setOptionDraft}
            />
          )}
        </Box>
      </DrawerShell>

      <ConfirmationBeforeRemoveModal
        open={isDeactivateModalOpen}
        title="Retire field?"
        content="Some cases may hold a value for this field. Retiring it removes the field from new data entry and from import mapping — those cases keep their values, and exports still include them."
        confirmButtonText="Retire field"
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setIsDeactivateModalOpen(false)}
        onClose={() => setIsDeactivateModalOpen(false)}
      />

      <ConfirmationBeforeRemoveModal
        open={isReactivateModalOpen}
        title="Restore custom field?"
        content="Restoring this field will make it available again for new data entry and import mapping. Existing case values are preserved."
        confirmButtonText="Restore"
        onConfirm={handleConfirmReactivate}
        onCancel={() => setIsReactivateModalOpen(false)}
        onClose={() => setIsReactivateModalOpen(false)}
      />
    </>
  );
};
